import React, { useMemo, useState } from 'react';
import { Box, Typography, IconButton, CircularProgress } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ZoomInOutlinedIcon from '@mui/icons-material/ZoomInOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import ListingImageLightbox from './ListingImageLightbox';
import { resolveAttachmentDisplayName } from '../../../utils/listingAttachmentUtils';

const ROW_BORDER = '1px solid rgba(255,255,255,0.10)';
const ROW_BG = 'rgba(255,255,255,0.035)';
const ROW_BG_HOVER = 'rgba(255,255,255,0.075)';
const ICON_WELL_BG = 'rgba(255,255,255,0.07)';
const ICON_WELL_BORDER = '1px solid rgba(255,255,255,0.12)';
const MUTED_ICON = 'rgba(255,255,255,0.68)';
const EMPTY_ATTACHMENTS = [];

/**
 * Lista de adjuntos UF como filas compactas, separada de la galería Fotos.
 * Las imágenes abren lightbox y los PDF se abren en una pestaña nueva.
 */
export default function ListingAttachments({
  attachments = [],
  canEdit = false,
  deletingIds = null,
  onDelete = null,
}) {
  const list = Array.isArray(attachments) ? attachments : EMPTY_ATTACHMENTS;
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const imageAttachments = useMemo(
    () => list.filter((item) => item?.kind === 'image' && item?.url),
    [list],
  );

  if (list.length === 0) return null;

  const openAttachment = (item) => {
    if (!item?.url) return;
    if (item.kind === 'pdf') {
      window.open(item.url, '_blank', 'noopener,noreferrer');
      return;
    }

    const idx = imageAttachments.findIndex(
      (img) => (img.id && item.id && img.id === item.id) || img.url === item.url,
    );
    if (idx >= 0) setLightboxIndex(idx);
  };

  const renderDeleteButton = (item, isDeleting) => {
    if (!canEdit || !onDelete) return null;
    return (
      <IconButton
        size="small"
        disabled={isDeleting}
        onClick={() => onDelete(item)}
        aria-label={`Eliminar adjunto: ${resolveAttachmentDisplayName(item)}`}
        sx={{
          flexShrink: 0,
          mr: 0.5,
          color: 'rgba(255,255,255,0.55)',
          '&:hover': {
            color: 'rgba(255,255,255,0.92)',
            bgcolor: 'rgba(255,255,255,0.10)',
          },
        }}
      >
        {isDeleting
          ? <CircularProgress size={15} sx={{ color: 'inherit' }} />
          : <DeleteOutlineIcon sx={{ fontSize: 18 }} />}
      </IconButton>
    );
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          width: '100%',
        }}
      >
        {list.map((item) => {
          const isPdf = item.kind === 'pdf';
          const isImage = item.kind === 'image';
          const isDeleting = deletingIds instanceof Set && item.id
            ? deletingIds.has(item.id)
            : false;
          const displayName = resolveAttachmentDisplayName(
            item,
            { fallback: isPdf ? 'Documento PDF' : 'Imagen adjunta' },
          );

          return (
            <Box
              key={item.id || item.url}
              sx={{
                display: 'flex',
                alignItems: 'center',
                minWidth: 0,
                border: ROW_BORDER,
                borderRadius: 1,
                bgcolor: ROW_BG,
                opacity: isDeleting ? 0.55 : 1,
                transition: 'background-color 0.15s ease, border-color 0.15s ease',
                '&:hover': {
                  bgcolor: ROW_BG_HOVER,
                  borderColor: 'rgba(255,255,255,0.18)',
                },
              }}
            >
              <Box
                component="button"
                type="button"
                disabled={isDeleting || !item.url}
                onClick={() => openAttachment(item)}
                aria-label={
                  isPdf
                    ? `Abrir PDF: ${displayName}`
                    : `Ampliar imagen adjunta: ${displayName}`
                }
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flex: 1,
                  minWidth: 0,
                  minHeight: 52,
                  px: 1,
                  py: 0.75,
                  border: 'none',
                  bgcolor: 'transparent',
                  color: '#fff',
                  cursor: isDeleting ? 'default' : (isImage ? 'zoom-in' : 'pointer'),
                  textAlign: 'left',
                  fontFamily: 'inherit',
                  '&:hover': { bgcolor: 'transparent' },
                  '&:focus-visible': {
                    outline: '2px solid rgba(255,255,255,0.55)',
                    outlineOffset: '-2px',
                  },
                }}
              >
                {isImage && item.url ? (
                  <Box
                    component="img"
                    src={item.url}
                    alt=""
                    sx={{
                      width: 34,
                      height: 34,
                      flexShrink: 0,
                      objectFit: 'cover',
                      display: 'block',
                      borderRadius: 0.75,
                      border: ICON_WELL_BORDER,
                      bgcolor: ICON_WELL_BG,
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 0.75,
                      bgcolor: ICON_WELL_BG,
                      border: ICON_WELL_BORDER,
                    }}
                  >
                    {isPdf ? (
                      <PictureAsPdfOutlinedIcon sx={{ fontSize: 22, color: MUTED_ICON }} />
                    ) : (
                      <InsertDriveFileOutlinedIcon sx={{ fontSize: 21, color: MUTED_ICON }} />
                    )}
                  </Box>
                )}

                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    component="span"
                    title={displayName}
                    sx={{
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: 'rgba(255,255,255,0.88)',
                      fontSize: '12px',
                      fontWeight: 600,
                      lineHeight: 1.3,
                    }}
                  >
                    {displayName}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      display: 'block',
                      mt: 0.25,
                      color: 'rgba(255,255,255,0.42)',
                      fontSize: '10px',
                      lineHeight: 1.2,
                    }}
                  >
                    {isPdf ? 'PDF' : 'Imagen'}
                  </Typography>
                </Box>

                {isPdf ? (
                  <OpenInNewIcon
                    aria-hidden="true"
                    sx={{ flexShrink: 0, fontSize: 17, color: 'rgba(255,255,255,0.48)' }}
                  />
                ) : (
                  <ZoomInOutlinedIcon
                    aria-hidden="true"
                    sx={{ flexShrink: 0, fontSize: 19, color: 'rgba(255,255,255,0.48)' }}
                  />
                )}
              </Box>
              {renderDeleteButton(item, isDeleting)}
            </Box>
          );
        })}
      </Box>

      {lightboxIndex != null ? (
        <ListingImageLightbox
          images={imageAttachments}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />
      ) : null}
    </>
  );
}

/** Empty-state visual cuando no hay adjuntos. */
export function ListingAttachmentsEmpty() {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.5,
        py: 2.5,
        borderRadius: 1.5,
        border: '1px dashed rgba(255,255,255,0.12)',
        bgcolor: 'rgba(255,255,255,0.02)',
      }}
    >
      <InsertDriveFileOutlinedIcon sx={{ fontSize: 22, color: 'rgba(255,255,255,0.28)' }} />
      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px' }}>
        Sin adjuntos
      </Typography>
    </Box>
  );
}
